import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useConfirm } from '../components/ConfirmDialog';
import '../components/Assets.css';
import './RollCall.css';

/**
 * The emergency roll-call (115; Gordon, 30 October). For an administrator,
 * on a phone at the assembly point: who might still be inside - the people
 * whose laptop last checked in from the office, today's expected guests -
 * with a big Safe button each, and who is still unaccounted for.
 *
 * It shows where LAPTOPS are, not people: it adds to the warden's sweep and
 * the visitor book, and says so at the top. Off until switched on.
 */
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const errorOf = (e, fallback) => e?.response?.data?.error || fallback;
const time = (t) => (t ? new Date(t).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '');
const day = (t) => (t ? new Date(t).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' }) : '');

const GROUPS = [
  ['seen', 'Seen in the office', (m) => `laptop checked in from the office in the last ${m} minutes`],
  ['possibly', 'Possibly in', () => 'laptop last checked in from the office earlier today'],
  ['visitor', 'Expected visitors', () => 'guests of a room meeting today'],
  ['added', 'Added by hand', () => 'not on the list when it started'],
  ['not_seen', 'Not seen in the office today', () => 'tick anybody who is here without a laptop']
];

export default function RollCall() {
  const [confirm, confirmDialog] = useConfirm();
  const [data, setData] = useState(null);
  const [rc, setRc] = useState(null);         // the open (or chosen) roll-call
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState('');
  const [extra, setExtra] = useState('');
  const [showNotSeen, setShowNotSeen] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API_BASE}/roll-call`);
      setData(r.data);
      if (r.data?.open) setRc((await axios.get(`${API_BASE}/roll-call/${r.data.open}`)).data);
      setError('');
    } catch (e) { setError(errorOf(e, 'Could not load the roll-call.')); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Several administrators may mark at once: keep the open one fresh.
  const openId = rc && !rc.ended_at ? rc.id : null;
  useEffect(() => {
    if (!openId) return undefined;
    const t = setInterval(() => {
      axios.get(`${API_BASE}/roll-call/${openId}`).then((r) => setRc(r.data)).catch(() => {});
    }, 10000);
    return () => clearInterval(t);
  }, [openId]);

  const act = async (fn, fallback) => {
    setBusy(true);
    setError('');
    try { const r = await fn(); return r; } catch (e) { setError(errorOf(e, fallback)); return null; } finally { setBusy(false); }
  };
  const switchOn = async () => {
    const ok = await confirm({ title: 'Switch on the emergency roll-call?', confirmLabel: 'Switch on',
      message: 'It uses where staff laptops last checked in from. Make sure the staff notice says so before switching it on.' });
    if (!ok) return;
    if (await act(() => axios.put(`${API_BASE}/app-settings/roll-call-enabled`, { value: true }), 'Could not switch it on.')) load();
  };
  const start = async (kind) => {
    if (kind === 'drill') {
      const ok = await confirm({ title: 'Start a fire drill roll-call?', confirmLabel: 'Start drill',
        message: 'The list as it stands now becomes the drill’s record.' });
      if (!ok) return;
    }
    const r = await act(() => axios.post(`${API_BASE}/roll-call`, { kind }), 'Could not start the roll-call.');
    if (r) { setRc(r.data); load(); }
  };
  const mark = async (p, status) => {
    const r = await act(() => axios.post(`${API_BASE}/roll-call/${rc.id}/people/${p.id}`, { status }), 'Could not mark that.');
    if (r) setRc(r.data);
  };
  const add = async () => {
    const name = extra.trim();
    if (!name) return;
    const r = await act(() => axios.post(`${API_BASE}/roll-call/${rc.id}/people`, { name, status: 'safe' }), 'Could not add them.');
    if (r) { setRc(r.data); setExtra(''); }
  };
  const end = async () => {
    const left = rc?.counts?.unaccounted || 0;
    const ok = await confirm({ title: 'End this roll-call?', confirmLabel: 'End it', danger: left > 0,
      message: left ? `${left} still unaccounted for. Ending keeps the record as it is.` : 'Everybody is accounted for. The record is kept 12 months.' });
    if (!ok) return;
    const r = await act(() => axios.post(`${API_BASE}/roll-call/${rc.id}/end`, {}), 'Could not end it.');
    if (r) { setRc(null); load(); }
  };

  const LIMITS = (
    <p className="as-note rc-limits" data-tone="warning" data-testid="rc-limits">
      This shows where <strong>laptops</strong> are, not people. A laptop left on a desk shows its owner as in; somebody
      whose laptop is asleep in a bag shows as out. Always sweep the floors and check the visitor book.
    </p>
  );

  if (error && !data) return <div className="container"><p className="as-note" data-tone="warning" role="alert">{error}</p></div>;
  if (!data) return <div className="container"><p className="as-quiet">Loading…</p></div>;

  if (!data.enabled) {
    return (
      <div className="container rc-page">
        <h1 className="page-title">Emergency roll-call</h1>
        <p className="page-description">
          In a fire or a drill: who might still be in the building, from where staff laptops last checked in, with
          today’s expected visitors - and a Safe button for each person at the assembly point. Administrators only;
          every opening is logged; records are kept 12 months.
        </p>
        <p className="as-note" data-tone="info" data-testid="rc-off">
          It is switched off. Turn it on once the staff notice says laptops’ office check-ins are used for the roll-call.
        </p>
        {(!data.reporting || data.location === 'off') && (
          <p className="as-note" data-tone="warning">
            Laptops are not reporting where they are ({!data.reporting ? 'device reporting is off' : 'location is off'}), so the
            list would be empty. See <Link to="/settings/assets/device-agent">Settings → Device agent</Link>.
          </p>
        )}
        {error && <p className="as-note" data-tone="warning" role="alert">{error}</p>}
        <button type="button" className="btn btn-primary" disabled={busy} onClick={switchOn}>Switch on the roll-call</button>
        {confirmDialog}
      </div>
    );
  }

  // The live lists before a roll-call starts, or the roll-call's own.
  const people = rc ? rc.people : GROUPS.flatMap(([g]) => (data.current?.[g] || []).map((p) => ({ ...p, grp: g, id: `${g}-${p.user_id || p.name}` })));
  const match = (p) => !q.trim() || `${p.name} ${p.place || ''}`.toLowerCase().includes(q.trim().toLowerCase());
  const inGroup = (g) => people.filter((p) => p.grp === g && match(p));

  return (
    <div className="container rc-page">
      <div className="rc-head">
        <h1 className="page-title">{rc ? (rc.kind === 'emergency' ? 'Emergency roll-call' : 'Fire drill roll-call') : 'Emergency roll-call'}</h1>
        {rc && <span className="as-quiet">{`Started ${time(rc.started_at)} by ${rc.started_by_name || 'an administrator'}`}</span>}
      </div>
      {LIMITS}
      {error && <p className="as-note" data-tone="warning" role="alert">{error}</p>}

      {!rc && (
        <div className="rc-start">
          <button type="button" className="btn rc-emergency" disabled={busy} onClick={() => start('emergency')} data-testid="rc-start-emergency">
            Start emergency roll-call
          </button>
          <button type="button" className="btn btn-secondary" disabled={busy} onClick={() => start('drill')}>Start a drill</button>
        </div>
      )}

      {rc && (
        <div className="rc-counts" data-testid="rc-counts">
          <div className="rc-count" data-tone="danger"><strong>{rc.counts.unaccounted}</strong><span>unaccounted</span></div>
          <div className="rc-count" data-tone="success"><strong>{rc.counts.safe}</strong><span>safe</span></div>
          <div className="rc-count"><strong>{rc.counts.not_here}</strong><span>not here</span></div>
        </div>
      )}

      <input type="search" className="rc-search" placeholder="Find a name or desk" value={q} onChange={(e) => setQ(e.target.value)}
        aria-label="Find a name or desk" />

      {GROUPS.map(([g, title, why]) => {
        const list = inGroup(g);
        if (!list.length && (g === 'added' || g === 'visitor')) return null;
        const folded = g === 'not_seen' && !showNotSeen && !q.trim();
        return (
          <section key={g} className="rc-group" data-group={g}>
            <h2>
              {`${title} (${list.length})`}
              <span className="as-quiet">{why(data.seen_minutes || 15)}</span>
            </h2>
            {g === 'not_seen' && !q.trim() && (
              <button type="button" className="btn btn-plain btn-small" onClick={() => setShowNotSeen((v) => !v)}>
                {showNotSeen ? 'Hide' : 'Show them'}
              </button>
            )}
            {!folded && (
              <ul className="rc-list">
                {list.map((p) => (
                  <li key={p.id} className="rc-person" data-status={p.status || 'none'} data-name={p.name}>
                    <div className="rc-who">
                      <strong>{p.name}</strong>
                      <span className="as-sub">{[p.place, p.last_seen_at && `seen ${time(p.last_seen_at)}`, p.detail].filter(Boolean).join(' · ')}</span>
                      {p.status && p.status !== 'unaccounted' && p.marked_by_name && (
                        <span className="as-sub">{`${p.status === 'safe' ? 'Safe' : 'Not here'} - ${p.marked_by_name}, ${time(p.marked_at)}`}</span>
                      )}
                    </div>
                    {rc && !rc.ended_at && (
                      <div className="rc-marks">
                        {p.status === 'unaccounted' ? (
                          <>
                            <button type="button" className="btn rc-safe" disabled={busy} onClick={() => mark(p, 'safe')}>Safe</button>
                            <button type="button" className="btn btn-plain btn-small" disabled={busy} onClick={() => mark(p, 'not_here')}>Not here</button>
                          </>
                        ) : (
                          <button type="button" className="btn btn-plain btn-small" disabled={busy} onClick={() => mark(p, 'unaccounted')}>Undo</button>
                        )}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>
        );
      })}

      {rc && !rc.ended_at && (
        <>
          <div className="as-inline-form rc-add">
            <input value={extra} onChange={(e) => setExtra(e.target.value)} placeholder="Somebody not on the list"
              aria-label="Name of somebody not on the list" maxLength={200}
              onKeyDown={(e) => { if (e.key === 'Enter') add(); }} />
            <button type="button" className="btn btn-secondary" disabled={busy || !extra.trim()} onClick={add}>Add as safe</button>
          </div>
          <p><button type="button" className="btn btn-danger" disabled={busy} onClick={end}>End the roll-call</button></p>
        </>
      )}

      {!rc && data.past?.length > 0 && (
        <section className="rc-past">
          <h2>Past roll-calls</h2>
          <ul className="as-tree">
            {data.past.map((p) => (
              <li key={p.id}>
                <button type="button" className="btn btn-plain btn-small"
                  onClick={() => axios.get(`${API_BASE}/roll-call/${p.id}`).then((r) => setRc(r.data)).catch(() => {})}>
                  {`${p.kind === 'emergency' ? 'Emergency' : 'Drill'} · ${day(p.started_at)}`}
                </button>
                <span className="as-quiet">{`${p.safe} safe · ${p.unaccounted} unaccounted${p.started_by_name ? ` · ${p.started_by_name}` : ''}`}</span>
              </li>
            ))}
          </ul>
          <p className="as-quiet">{`Kept ${data.keep_months || 12} months, then deleted.`}</p>
        </section>
      )}
      {rc && rc.ended_at && (
        <p>
          <span className="as-quiet">{`Ended ${day(rc.ended_at)}${rc.ended_by_name ? ` by ${rc.ended_by_name}` : ''}.`}</span>{' '}
          <button type="button" className="btn btn-plain btn-small" onClick={() => setRc(null)}>Back</button>
        </p>
      )}
      {confirmDialog}
    </div>
  );
}
